import { motion } from 'framer-motion'
import { MessageSquare } from 'lucide-react'
import LiveChatSettings from '@/components/settings/LiveChatSettings'
import TeamAgentsView from '@/components/settings/TeamAgentsView'
import { useSearchParams } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import { useState, useEffect } from 'react'
import { useProgramContext } from '@/context/ProgramContext'
import { canManageProjectSettings, canWriteFeedback } from '@/lib/programPermissions'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'

const LiveChat = () => {
	const { t } = useTranslation()
	const [searchParams, setSearchParams] = useSearchParams()
	const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'agent')
	const { currentProgramAccessLevel, currentProgram, loading: isProgramLoading } = useProgramContext()

	useEffect(() => {
		const tabFromUrl = searchParams.get('tab')
		if (tabFromUrl && tabFromUrl !== activeTab) {
			setActiveTab(tabFromUrl)
		}
	}, [searchParams])

	const hasAdminAccess = canManageProjectSettings(currentProgramAccessLevel)
	const hasWriteAccess = canWriteFeedback(currentProgramAccessLevel)
	const hasReadAccess = currentProgramAccessLevel === 'read'

	const canEditOwnProfile = hasWriteAccess
	const canManageOtherAgents = hasAdminAccess

	// Show read-only view for users with read-only access
	if (hasReadAccess) {
		return (
			<div className="min-h-screen p-3 sm:p-4 md:p-8 max-w-5xl mx-auto space-y-4 sm:space-y-6">
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3 }}
					className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-4 sm:p-6 shadow-sm border border-border/50">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-primary/10 rounded-lg">
							<MessageSquare className="h-5 w-5 text-primary" />
						</div>
						<div>
							<h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground">
								{t('liveChat.liveChatSupportTeam')}
							</h1>
							<p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
								{t('liveChat.agentsAvailable')}
							</p>
						</div>
					</div>
				</motion.div>

				{currentProgram?.id ? (
					<TeamAgentsView programId={currentProgram.id} />
				) : (
					<div className="flex items-center justify-center mt-16">
						<div className="text-center max-w-md">
							<MessageSquare className="h-16 w-16 text-muted-foreground/50 mb-4 mx-auto" />
							<h2 className="text-lg font-semibold text-foreground mb-2">
								{t('liveChat.noProgramSelected')}
							</h2>
							<p className="text-muted-foreground">
								{t('liveChat.selectProgramToView')}
							</p>
						</div>
					</div>
				)}
			</div>
		)
	}

	if (isProgramLoading) {
		return (
			<div className="min-h-screen p-3 sm:p-4 md:p-8 max-w-5xl mx-auto space-y-6">
				<div className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-4 sm:p-6 shadow-sm border border-border/50">
					<div className="flex items-center gap-3">
						<Skeleton className="h-9 w-9 rounded-lg" />
						<div className="space-y-2 flex-1">
							<Skeleton className="h-6 w-32" />
							<Skeleton className="h-4 w-48 sm:w-64" />
						</div>
					</div>
				</div>

				<div className="space-y-6">
					<Skeleton className="h-10 w-full max-w-sm" />

					<div className="border rounded-lg p-4 sm:p-6 space-y-6">
						<div className="space-y-2">
							<Skeleton className="h-6 w-40" />
							<Skeleton className="h-4 w-64" />
						</div>

						<div className="flex items-center gap-4 sm:gap-6">
							<Skeleton className="h-20 w-20 sm:h-24 sm:w-24 rounded-full" />
							<div className="space-y-2">
								<Skeleton className="h-9 w-28" />
								<Skeleton className="h-3 w-36" />
							</div>
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div className="space-y-2">
								<Skeleton className="h-4 w-20" />
								<Skeleton className="h-10 w-full" />
							</div>
							<div className="space-y-2">
								<Skeleton className="h-4 w-20" />
								<Skeleton className="h-10 w-full" />
							</div>
						</div>

						<div className="space-y-2">
							<Skeleton className="h-4 w-16" />
							<Skeleton className="h-20 w-full" />
						</div>
					</div>
				</div>
			</div>
		)
	}

	// For team members with write/admin access, show the agent settings
	// They can manage their own agent profile and team settings
	return (
		<div className="min-h-screen overflow-y-auto p-3 sm:p-4 md:p-8 max-w-5xl mx-auto space-y-4 sm:space-y-6">
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3 }}
				className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-4 sm:p-6 shadow-sm border border-border/50 mb-6">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/10 rounded-lg">
						<MessageSquare className="h-5 w-5 text-primary" />
					</div>
					<div>
						<h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground">
							{t('liveChat.liveChat')}
						</h1>
						<p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
							{hasAdminAccess
								? t('liveChat.manageAgentProfilesAdmin')
								: hasWriteAccess
								? t('liveChat.setupAgentProfile')
								: t('liveChat.viewLiveChatAgents')}
						</p>
					</div>
				</div>
			</motion.div>

			{canManageOtherAgents ? (
				// Admins see a tabbed interface
				<div className="space-y-6">
					<div className="inline-flex h-10 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground">
						<button
							onClick={() => {
								setActiveTab('agent')
								setSearchParams({ tab: 'agent' })
							}}
							className={cn(
								"inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
								activeTab === 'agent'
									? 'bg-background text-foreground shadow-sm'
									: 'hover:bg-background/50 hover:text-foreground'
							)}>
							{t('liveChat.myProfile')}
						</button>
						<button
							onClick={() => {
								setActiveTab('team')
								setSearchParams({ tab: 'team' })
							}}
							className={cn(
								"inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
								activeTab === 'team'
									? 'bg-background text-foreground shadow-sm'
									: 'hover:bg-background/50 hover:text-foreground'
							)}>
							{t('liveChat.manageAgents')}
						</button>
					</div>
					{activeTab === 'team' ? (
						<div className="space-y-6">
							<TeamAgentsView
								programId={currentProgram?.id}
								showEditControls={true}
							/>
						</div>
					) : (
						<LiveChatSettings
							defaultTab={activeTab}
							programId={currentProgram?.id}
							showTeamManagement={false}
						/>
					)}
				</div>
			) : canEditOwnProfile ? (
				<LiveChatSettings
					defaultTab={activeTab}
					programId={currentProgram?.id}
					showTeamManagement={false}
				/>
			) : (
				<TeamAgentsView programId={currentProgram?.id} />
			)}
		</div>
	)
}

export default LiveChat
